import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, X, Check, ShieldCheck, Loader2, Send, Building2, ClipboardList } from "lucide-react";
import {
  chGetFormularioPublico, chGuardarFormularioPublico, chCompletarFormularioPublico,
  chSubirFotoFormulario, chBorrarFotoFormulario, fotoUrl,
} from "@/lib/prevelis";

const field = "w-full mt-1 h-11 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

export default function FormularioPublicoCH() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idioma, setIdioma] = useState("es");
  const [datos, setDatos] = useState({});
  const [perfil, setPerfil] = useState({});
  const [saving, setSaving] = useState(false);
  const [completado, setCompletado] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    chGetFormularioPublico(token).then((d) => {
      setData(d);
      const f = d.formulario;
      setIdioma(f.idioma || "es");
      setDatos(f.datos || {});
      setPerfil(f.perfil || {});
      setCompletado(f.estado === "completado" || f.estado === "revisado");
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("No disponible"); });
  }, [token]);

  const autosave = useCallback((patch) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSaving(true);
      try { await chGuardarFormularioPublico(token, patch); }
      catch (e) { console.error("Autosave CH falló (se reintentará):", e); }
      finally { setSaving(false); }
    }, 900);
  }, [token]);

  const setDato = (k, v) => { const n = { ...datos, [k]: v }; setDatos(n); autosave({ datos: { [k]: v } }); };
  const setPerf = (k, v) => { const n = { ...perfil, [k]: v }; setPerfil(n); autosave({ perfil: { [k]: v } }); };
  const cambiarIdioma = (lng) => { setIdioma(lng); autosave({ idioma: lng }); };

  const onFoto = async (file) => {
    try {
      const foto = await chSubirFotoFormulario(token, file);
      setData((d) => ({ ...d, formulario: { ...d.formulario, fotos: [...(d.formulario.fotos || []), foto] } }));
    } catch { toast.error(idioma === "fr" ? "Échec du téléversement" : "No se pudo subir la fotografía"); }
  };
  const delFoto = async (fid) => {
    try { await chBorrarFotoFormulario(token, fid); } catch (e) { console.error(e); }
    setData((d) => ({ ...d, formulario: { ...d.formulario, fotos: (d.formulario.fotos || []).filter((f) => f.id !== fid) } }));
  };

  const finalizar = async () => {
    try {
      await chGuardarFormularioPublico(token, { datos, perfil, idioma });
      await chCompletarFormularioPublico(token);
      setCompletado(true); window.scrollTo(0, 0);
    } catch { toast.error(idioma === "fr" ? "Impossible d'envoyer" : "No se pudo finalizar"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center text-slate-500">—</div>;

  const t = data.ui[idioma];
  const fotos = data.formulario.fotos || [];

  if (completado) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md text-center" data-testid="ch-form-completado">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-emerald-600" /></div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">{t.enviado_titulo}</h1>
          <p className="text-sm text-slate-500">{t.enviado_texto}</p>
        </div>
      </div>
    );
  }

  const selCls = (on) => `h-8 px-3 rounded-lg text-sm font-medium border ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300"}`;

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-700" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-800 truncate">PREVELIS · Suisse romande 🇨🇭</p>
            <p className="text-xs text-slate-400 truncate">{t.titulo}</p>
          </div>
          <div className="flex gap-1" data-testid="ch-form-idioma">
            <button onClick={() => cambiarIdioma("es")} data-testid="ch-form-idioma-es" className={selCls(idioma === "es")}>ES</button>
            <button onClick={() => cambiarIdioma("fr")} data-testid="ch-form-idioma-fr" className={selCls(idioma === "fr")}>FR</button>
          </div>
          <span className="text-xs text-slate-400 ml-2 hidden sm:flex items-center gap-1">{saving ? <><Loader2 className="w-3 h-3 animate-spin" /> {t.guardando}</> : t.guardado}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        <p className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">{t.intro}</p>

        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ch-form-datos">
          <div className="flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-blue-700" /><p className="font-medium text-slate-800 text-sm">{t.datos_empresa}</p></div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.campos_empresa.map((c) => (
              <label key={c.k} className={c.tipo === "select_tipo" || c.tipo === "select_canton" ? "block sm:col-span-1" : "block"}>
                <span className="text-xs text-slate-500">{c[idioma]}</span>
                {c.tipo === "select_canton" ? (
                  <select value={datos[c.k] || ""} onChange={(e) => setDato(c.k, e.target.value)} data-testid={`ch-form-d-${c.k}`} className={field}>
                    <option value="">—</option>
                    {data.cantones.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                ) : c.tipo === "select_tipo" ? (
                  <select value={datos[c.k] || ""} onChange={(e) => setDato(c.k, e.target.value)} data-testid={`ch-form-d-${c.k}`} className={field}>
                    <option value="">—</option>
                    {data.tipos_organizacion.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                ) : (
                  <input type={c.tipo === "number" ? "number" : "text"} value={datos[c.k] || ""} onChange={(e) => setDato(c.k, e.target.value)} data-testid={`ch-form-d-${c.k}`} className={field} />
                )}
              </label>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ch-form-perfil">
          <div className="flex items-center gap-2 mb-3"><ClipboardList className="w-4 h-4 text-blue-700" /><p className="font-medium text-slate-800 text-sm">{t.perfil}</p></div>
          <div className="space-y-3">
            {data.campos_perfil.map((c) => (
              <label key={c.k} className="block">
                <span className="text-xs text-slate-500">{c[idioma]}</span>
                <textarea rows={2} value={perfil[c.k] || ""} placeholder={idioma === "fr" ? c.ph_fr : c.ph_es}
                  onChange={(e) => setPerf(c.k, e.target.value)} data-testid={`ch-form-p-${c.k}`}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
              </label>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ch-form-fotos">
          <div className="flex items-center gap-2 mb-3"><Camera className="w-4 h-4 text-blue-700" /><p className="font-medium text-slate-800 text-sm">{t.fotos}</p></div>
          <div className="flex flex-wrap gap-2">
            {fotos.map((f) => (
              <div key={f.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                <img src={fotoUrl(f.url)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => delFoto(f.id)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 cursor-pointer hover:border-blue-400" data-testid="ch-form-foto-add">
              <Camera className="w-5 h-5" />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) onFoto(e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>
        </section>
      </main>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-20">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center">
          <button onClick={finalizar} data-testid="ch-form-finalizar" className="ml-auto inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-blue-700 text-white font-medium hover:bg-blue-800">
            <Send className="w-4 h-4" /> {t.enviar}
          </button>
        </div>
      </div>
    </div>
  );
}
